import { ARSuite, PROCEDURAL_PRODUCTS, serializeAnchorSpec, type AnchorSpec, type TrackingSnapshot } from '@ar-suite/sdk-web'
import { PROCEDURAL_EARRINGS } from '@ar-suite/module-earrings'
import { PROCEDURAL_FACE_PROPS } from '@ar-suite/module-face-props'
import { PROCEDURAL_NECKLACES } from '@ar-suite/module-necklace'
import { PROCEDURAL_RINGS } from '@ar-suite/module-ring'
import { PROCEDURAL_WRIST } from '@ar-suite/module-wrist'
import { defaultAnchorSpec } from '@ar-suite/core'

const $ = <T extends HTMLElement>(sel: string): T => {
  const el = document.querySelector<T>(sel)
  if (!el) throw new Error(`missing ${sel}`)
  return el
}

const stage = $('#stage')
const hint = $('#hint')
const hudStatus = $('#hud-status')
const hudFps = $('#hud-fps')
const hudInf = $('#hud-inf')
const productSel = $<HTMLSelectElement>('#product')
const slidersEl = $('#sliders')
const anchorJsonEl = $('#anchor-json')
const dbgInfo = $('#dbg-info')

let ar: ARSuite | null = null
let spec: AnchorSpec | null = null
let currentGlbUrl: string | null = null

// ---------- product picker ----------
const JEWELRY = PROCEDURAL_FACE_PROPS.filter((p) => p.category !== 'hat')
const HATS = PROCEDURAL_FACE_PROPS.filter((p) => p.category === 'hat')
const ALL_PRODUCTS = [...PROCEDURAL_PRODUCTS, ...PROCEDURAL_EARRINGS, ...PROCEDURAL_FACE_PROPS, ...PROCEDURAL_NECKLACES, ...PROCEDURAL_RINGS, ...PROCEDURAL_WRIST]
for (const [label, items] of [
  ['Glasses', PROCEDURAL_PRODUCTS],
  ['Earrings', PROCEDURAL_EARRINGS],
  ['Nose pin · Bindi · Tikka', JEWELRY],
  ['Hats · Headphones', HATS],
  ['Necklaces', PROCEDURAL_NECKLACES],
  ['Rings', PROCEDURAL_RINGS],
  ['Watches · Bangles', PROCEDURAL_WRIST],
] as const) {
  const grp = document.createElement('optgroup')
  grp.label = label
  for (const p of items) {
    const opt = document.createElement('option')
    opt.value = p.id
    opt.textContent = p.name
    grp.appendChild(opt)
  }
  productSel.appendChild(grp)
}

// ---------- fit sliders ----------
interface SliderDef {
  key: string
  label: string
  min: number
  max: number
  step: number
  get: (s: AnchorSpec) => number
  set: (s: AnchorSpec, v: number) => void
}
const SLIDERS: SliderDef[] = [
  { key: 'ox', label: 'offset x', min: -3, max: 3, step: 0.05, get: (s) => s.offset[0], set: (s, v) => (s.offset[0] = v) },
  { key: 'oy', label: 'offset y', min: -3, max: 3, step: 0.05, get: (s) => s.offset[1], set: (s, v) => (s.offset[1] = v) },
  { key: 'oz', label: 'offset z', min: -3, max: 4, step: 0.05, get: (s) => s.offset[2], set: (s, v) => (s.offset[2] = v) },
  { key: 'rx', label: 'rot x (pantoscopic)', min: -25, max: 25, step: 0.5, get: (s) => s.rotation[0], set: (s, v) => (s.rotation[0] = v) },
  { key: 'ry', label: 'rot y', min: -25, max: 25, step: 0.5, get: (s) => s.rotation[1], set: (s, v) => (s.rotation[1] = v) },
  { key: 'rz', label: 'rot z', min: -25, max: 25, step: 0.5, get: (s) => s.rotation[2], set: (s, v) => (s.rotation[2] = v) },
  { key: 'scale', label: 'scale', min: 0.6, max: 1.5, step: 0.01, get: (s) => s.scale, set: (s, v) => (s.scale = v) },
  { key: 'occ', label: 'occluder depth', min: -2, max: 3, step: 0.1, get: (s) => s.occluder.depthOffset, set: (s, v) => (s.occluder.depthOffset = v) },
  { key: 'occs', label: 'occluder head scale', min: 0.8, max: 1.2, step: 0.01, get: (s) => s.occluder.scale, set: (s, v) => (s.occluder.scale = v) },
  { key: 'lens', label: 'lens opacity', min: 0, max: 1, step: 0.02, get: (s) => s.glasses?.lensOpacity ?? 0.18, set: (s, v) => ((s.glasses ??= {}).lensOpacity = v) },
]
const sliderInputs = new Map<string, { input: HTMLInputElement; val: HTMLElement }>()

function buildSliders(): void {
  slidersEl.innerHTML = ''
  for (const d of SLIDERS) {
    const row = document.createElement('div')
    row.className = 'slider'
    const label = document.createElement('span')
    label.textContent = d.label
    const input = document.createElement('input')
    input.type = 'range'
    input.min = String(d.min)
    input.max = String(d.max)
    input.step = String(d.step)
    const val = document.createElement('span')
    val.className = 'val'
    input.addEventListener('input', () => {
      if (!spec) return
      d.set(spec, Number(input.value))
      val.textContent = Number(input.value).toFixed(2)
      applySpec()
    })
    row.append(label, input, val)
    slidersEl.appendChild(row)
    sliderInputs.set(d.key, { input, val })
  }
}
function syncSliders(): void {
  if (!spec) return
  for (const d of SLIDERS) {
    const s = sliderInputs.get(d.key)!
    const v = d.get(spec)
    s.input.value = String(v)
    s.val.textContent = v.toFixed(2)
  }
  anchorJsonEl.textContent = serializeAnchorSpec(spec)
}
function applySpec(): void {
  if (!ar || !spec) return
  ar.updateSpec(spec)
  anchorJsonEl.textContent = serializeAnchorSpec(spec)
}
buildSliders()

// ---------- SDK lifecycle ----------
async function start(): Promise<void> {
  const btn = $<HTMLButtonElement>('#btn-start')
  btn.disabled = true
  btn.textContent = 'Loading model…'
  hint.textContent = 'Loading face model…'
  try {
    ar = await ARSuite.init({
      modules: ['glasses', 'earrings', 'jewelry', 'hats', 'necklace', 'rings', 'wrist'],
      assets: {
        wasmPath: '/wasm',
        faceModelPath: '/models/face_landmarker.task',
        // ear model (earnet v9, trained on real ears) is DEFAULT ON; ?ears=off disables, ?ears=v0 loads the synthetic v0
        ...(() => {
          const m = new URLSearchParams(location.search).get('ears')
          if (m === 'off') return { earModelPath: '/models/__disabled__.onnx' }
          if (m === 'v0') return { earModelPath: '/models/ear_v0.onnx', earModelContract: 'v0' as const }
          return {}
        })(),
      },
      camera: { facing: 'user', width: 1280, height: 720 },
      // ?delegate=cpu forces the CPU delegate (useful for headless/CI or GPU-less devices)
      tracker: { delegate: new URLSearchParams(location.search).get('delegate') === 'cpu' ? 'CPU' : 'GPU' },
      smoothing: ($<HTMLSelectElement>('#smoothing').value as 'balanced'),
    })
    ar.on('tracking', onTracking)
    ar.on('error', (e) => console.error('[ar]', e))
    hint.textContent = 'Starting camera…'
    await ar.mount(stage)
    hint.textContent = 'Show your face'
    onTracking(ar.tracking.current)
    btn.textContent = 'Camera running'
    $<HTMLButtonElement>('#btn-capture').disabled = false
    $<HTMLButtonElement>('#btn-flip').disabled = false
    await loadProduct(productSel.value)
    requestAnimationFrame(hudLoop)
  } catch (e) {
    console.error(e)
    hint.textContent = `Error: ${(e as Error).message}`
    btn.disabled = false
    btn.textContent = 'Start camera'
  }
}

function onTracking(s: TrackingSnapshot): void {
  hudStatus.textContent = s.status
  hudStatus.className = `pill ${s.status}`
  hint.textContent = s.status === 'tracking' ? '' : s.status === 'lost' ? 'Face lost — hold still' : 'Show your face'
}

function hudLoop(): void {
  if (!ar) return
  const st = ar.stats
  hudFps.textContent = `${st.fps} fps`
  hudInf.textContent = `inf ${st.inferenceMs.toFixed(1)} ms · draw ${st.renderMs.toFixed(1)} ms`
  if (spec?.category === 'earring' && ar.earrings?.lastResult) {
    const e = ar.earrings.lastResult
    dbgInfo.textContent =
      `loop: ${st.loopMode} · matrix layout: ${ar.tracker.matrixLayout}\n` +
      `lobe L: ${e.left.pose.position.map((v) => v.toFixed(1)).join(', ')} cm\n` +
      `lobe R: ${e.right.pose.position.map((v) => v.toFixed(1)).join(', ')} cm\n` +
      `ear tracker: ${ar.earTracker ? 'on' : 'off (fallback)'} · observed weight L/R: ${e.observedWeight.left.toFixed(2)} / ${e.observedWeight.right.toFixed(2)}`
    requestAnimationFrame(hudLoop)
    return
  }
  if ((spec?.category === 'ring' || spec?.category === 'bracelet' || spec?.category === 'watch') && ar) {
    const mod = spec.category === 'ring' ? ar.rings : ar.wrist
    const h = mod?.lastHand
    const r = mod?.lastResult
    dbgInfo.textContent = h && r
      ? `hand: ${h.handedness} · depth ${h.depth.toFixed(0)} cm · knuckle span ${h.knuckleSpanCm.toFixed(1)} cm\n` +
        ('fingerDiameterCm' in r ? `finger ⌀ ${(r as { fingerDiameterCm: number }).fingerDiameterCm.toFixed(2)} cm` :
         `wrist ${(r as { wristWidthCm: number }).wristWidthCm.toFixed(1)} cm · forearm weight ${(r as { forearmWeight: number }).forearmWeight.toFixed(2)}`)
      : 'show a hand to the camera'
    requestAnimationFrame(hudLoop)
    return
  }
  if (spec?.category === 'necklace' && ar.necklace?.lastResult) {
    const n = ar.necklace.lastResult
    dbgInfo.textContent =
      `loop: ${st.loopMode} · pose tracker: ${ar.poseTracker?.ready ? 'on' : 'off'}\n` +
      `notch: ${n.pose.position.map((v) => v.toFixed(1)).join(', ')} cm · shoulder weight ${n.shoulderWeight.toFixed(2)}\n` +
      `shoulder width ${n.chest.width.toFixed(1)} cm · roll ${((n.chest.roll * 180) / Math.PI).toFixed(1)}°`
    requestAnimationFrame(hudLoop)
    return
  }
  const r = ar.glasses?.lastResult
  if (r) {
    dbgInfo.textContent =
      `loop: ${st.loopMode} · matrix layout: ${ar.tracker.matrixLayout}\n` +
      `pos: ${r.pose.position.map((v) => v.toFixed(1)).join(', ')} cm\n` +
      `ipd: ${r.fit.ipd.toFixed(2)} cm · head width: ${r.fit.headWidth.toFixed(1)} cm\n` +
      `temple L/R: ${r.fit.templeLengthLeft.toFixed(1)} / ${r.fit.templeLengthRight.toFixed(1)} cm`
  }
  requestAnimationFrame(hudLoop)
}

async function loadProduct(id: string): Promise<void> {
  if (!ar) return
  currentGlbUrl = null
  const p = ALL_PRODUCTS.find((x) => x.id === id)
  if (!p) return
  spec = structuredClone(p.anchor)
  await ar.tryOn({ product: { id: p.id, name: p.name, anchor: spec, procedural: p.procedural } })
  syncSliders()
}

async function loadGlb(url: string, name: string): Promise<void> {
  if (!ar) return
  currentGlbUrl = url
  spec = defaultAnchorSpec('glasses')
  spec.meta = { name }
  hint.textContent = 'Loading GLB…'
  try {
    await ar.tryOn({ glbUrl: url, anchor: spec })
    hint.textContent = ''
    syncSliders()
  } catch (e) {
    hint.textContent = `GLB failed: ${(e as Error).message}`
  }
}

// ---------- UI wiring ----------
$('#btn-start').addEventListener('click', () => void start())
productSel.addEventListener('change', () => void loadProduct(productSel.value))
$('#btn-load-url').addEventListener('click', () => {
  const url = $<HTMLInputElement>('#glb-url').value.trim()
  if (url) void loadGlb(url, url.split('/').pop() ?? 'model.glb')
})
$<HTMLInputElement>('#glb-file').addEventListener('change', (ev) => {
  const file = (ev.target as HTMLInputElement).files?.[0]
  if (file) void loadGlb(URL.createObjectURL(file), file.name)
})
$('#btn-reset-fit').addEventListener('click', () => {
  if (!spec) return
  const p = ALL_PRODUCTS.find((x) => x.id === productSel.value)
  const fresh = currentGlbUrl || !p ? defaultAnchorSpec('glasses') : structuredClone(p.anchor)
  Object.assign(spec, fresh)
  applySpec()
  syncSliders()
})
$('#btn-export').addEventListener('click', () => {
  if (!spec) return
  const blob = new Blob([serializeAnchorSpec(spec)], { type: 'application/json' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = 'anchor.json'
  a.click()
  URL.revokeObjectURL(a.href)
})
$('#btn-copy').addEventListener('click', () => {
  if (spec) void navigator.clipboard.writeText(serializeAnchorSpec(spec))
})
$('#btn-capture').addEventListener('click', async () => {
  if (!ar) return
  const blob = await ar.capture({ type: 'image/jpeg', quality: 0.92 })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = `ar-suite-${Date.now()}.jpg`
  a.click()
  URL.revokeObjectURL(a.href)
})
$('#btn-flip').addEventListener('click', () => void ar?.switchCamera())
$<HTMLSelectElement>('#smoothing').addEventListener('change', (ev) => {
  const v = (ev.target as HTMLSelectElement).value as 'balanced'
  ar?.glasses?.setSmoothing(v)
  ar?.earrings?.setSmoothing(v)
})
const debugFlags = () => ({
  landmarks: $<HTMLInputElement>('#dbg-landmarks').checked,
  tesselation: $<HTMLInputElement>('#dbg-tess').checked,
  occluder: $<HTMLInputElement>('#dbg-occluder').checked,
  anchors: $<HTMLInputElement>('#dbg-anchors').checked,
  axes: $<HTMLInputElement>('#dbg-axes').checked,
})
for (const id of ['#dbg-landmarks', '#dbg-tess', '#dbg-occluder', '#dbg-anchors', '#dbg-axes'])
  $(id).addEventListener('change', () => ar?.setDebug(debugFlags()))

// expose for console poking
Object.defineProperty(window, 'ar', { get: () => ar })
